// src/pages/stock/Issuestock.js - Complete with Cache + Instant Load + City Filter + City Counts

import React, { useState, useEffect } from 'react';
import { FaBoxes, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaMapMarkerAlt } from 'react-icons/fa';
import { FaBoxTissue } from "react-icons/fa6";
import { RiDeleteBin5Fill } from "react-icons/ri";
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { IoMdArrowDropdown } from "react-icons/io";
import api from '../../services/api';
import './Issuestock.css';

// ============================================================
// CACHE HELPERS
// ============================================================
const CACHE_PREFIX = 'issuestock_cache_';

const getCachedIssuestock = (shopId, city) => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${shopId}_${city || 'all'}`);
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
};

const setCachedIssuestock = (shopId, city, data) => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${shopId}_${city || 'all'}`, JSON.stringify(data));
  } catch (e) {}
};

const clearIssuestockCache = (shopId) => {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`${CACHE_PREFIX}${shopId}_`)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {}
};

export const prefetchAllShopsIssuestock = async () => {
  try {
    const shopsRes = await api.get('/user/shops/');
    const shops = Array.isArray(shopsRes.data) ? shopsRes.data : [];

    await Promise.all(
      shops.map(async (shop) => {
        try {
          const res = await api.get(`/inventory/materials/?shop_id=${shop.shop_id}`);
          const records = Array.isArray(res.data) ? res.data : [];
          setCachedIssuestock(shop.shop_id, null, records);
          console.log(`✅ Prefetched ${records.length} issuestock records for shop ${shop.shop_id}`);
        } catch (err) {}
      })
    );
    console.log('✅ All shops issuestock prefetched');
  } catch (err) {}
};

// city compare helper (case-insensitive, trimmed)
const normCity = (c) => String(c || '').trim().toLowerCase();

const Issuestock = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  // NOTE: holds ALL records of the shop (city filter is applied on frontend so counts stay correct)
  const [issuestockData, setIssuestockData] = useState([]);
  const [error, setError] = useState(null);
  const [shops, setShops] = useState([]);

  const [selectedShop, setSelectedShop] = useState(() => {
    return localStorage.getItem('selectedShopId') || '';
  });
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadedShopId, setLoadedShopId] = useState(null);

  const [dataLoaded, setDataLoaded] = useState(false);

  // ============================================================
  // INITIAL LOAD
  // ============================================================
  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) {
      const shopIdFromUrl = match[1];
      if (selectedShop !== shopIdFromUrl) {
        setSelectedShop(shopIdFromUrl);
        localStorage.setItem('selectedShopId', shopIdFromUrl);
      }
    } else {
      const storedShopId = localStorage.getItem('selectedShopId');
      if (storedShopId && selectedShop !== storedShopId) {
        setSelectedShop(storedShopId);
      }
    }
  }, [location.pathname]);

  // City change ab refetch nahi karta - sirf shop change par fetch hota hai
  useEffect(() => {
    if (selectedShop) {
      const cached = getCachedIssuestock(selectedShop, null);
      if (cached && cached.length > 0) {
        setIssuestockData(cached);
        setDataLoaded(true);
      } else {
        setIssuestockData([]);
        setDataLoaded(false);
      }
      setLoadedShopId(selectedShop);

      fetchIssuestockData();
    }
  }, [selectedShop]);

  // Header city dropdown ke liye cities load karo (shop change par)
  useEffect(() => {
    if (!selectedShop) return;
    setCities([]);
    const loadCities = async () => {
      try {
        setLoadingCities(true);
        const response = await api.get(`/inventory/shop-cities/?shop_id=${selectedShop}`);
        let list = [];
        if (response.data) {
          if (Array.isArray(response.data.cities)) {
            list = response.data.cities;
          } else if (Array.isArray(response.data)) {
            list = response.data;
          }
        }
        if (list.length > 0) setCities(list);
      } catch (err) {
        // API na ho to data se cities nikal li jayengi
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [selectedShop]);

  useEffect(() => {
    const handleShopChangeEvent = (e) => {
      if (e.detail && e.detail.shopId) {
        const newShopId = e.detail.shopId.toString();
        setSelectedShop(newShopId);
        localStorage.setItem('selectedShopId', newShopId);

        const cached = getCachedIssuestock(newShopId, '');
        if (cached && cached.length > 0) {
          setIssuestockData(cached);
          setDataLoaded(true);
        } else {
          setIssuestockData([]);
          setDataLoaded(false);
        }
        setLoadedShopId(newShopId);
      }
    };

    window.addEventListener('shopChanged', handleShopChangeEvent);
    return () => {
      window.removeEventListener('shopChanged', handleShopChangeEvent);
    };
  }, []);

  const fetchShops = async () => {
    try {
      const response = await api.get('/user/shops/');
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  // ============================================================
  // FETCH: poora shop ka data (city filter frontend par lagta hai)
  // ============================================================
  const fetchIssuestockData = async () => {
    try {
      setError(null);

      let url = '/inventory/materials/';
      const params = new URLSearchParams();

      if (selectedShop) {
        params.append('shop_id', selectedShop);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url);
      const records = Array.isArray(response.data) ? response.data : [];

      setIssuestockData(records);
      setDataLoaded(true);

      // Agar API se cities nahi aayin to data se dropdown bhar do
      const uniqueCities = [...new Set(records.map((r) => r.city).filter(Boolean))];
      setCities((prev) => (prev.length > 0 ? prev : uniqueCities));

      if (selectedShop) {
        setCachedIssuestock(selectedShop, null, records);
      }
    } catch (error) {
      console.error('Error fetching issue stock data:', error);
      setError('Failed to load data. Please try again.');
      setDataLoaded(true);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // ✅ Count of records per city (always from full shop data)
  const cityCounts = issuestockData.reduce((acc, item) => {
    if (item.city) {
      const key = normCity(item.city);
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});

  const totalCount = issuestockData.length;
  const isAllCities = !selectedCity || selectedCity === 'all';
  const selectedCityCount = isAllCities ? totalCount : (cityCounts[normCity(selectedCity)] || 0);
  const selectedCityLabel = isAllCities ? 'All Cities' : selectedCity;

  // Final filter: city + search
  const filteredData = issuestockData.filter((item) => {
    if (!isAllCities && normCity(item.city) !== normCity(selectedCity)) {
      return false;
    }

    const term = searchTerm.toLowerCase();
    return (
      item.internal_lot_number?.toLowerCase().includes(term) ||
      item.internal_supplier_number?.toLowerCase().includes(term) ||
      item.lot_number?.toLowerCase().includes(term) ||
      item.internal_lot_no?.toLowerCase().includes(term) ||
      item.material?.toLowerCase().includes(term)
    );
  });

  const handleSelectAll = () => {
    if (selectedItems.length === filteredData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredData.map((item) => item.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Selection',
        text: 'Please select items to delete',
        confirmButtonColor: '#004aad',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Selected Items?',
      text: `Are you sure you want to delete ${selectedItems.length} item(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const deletePromises = selectedItems.map((id) =>
          api.delete(`/inventory/materials/${id}/delete/`)
        );
        await Promise.all(deletePromises);

        clearIssuestockCache(selectedShop);
        await fetchIssuestockData();
        setSelectedItems([]);
        setShowActionDropdown(false);

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `${selectedItems.length} item(s) deleted successfully!`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error deleting items:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete items. Please try again.',
          confirmButtonColor: '#dc3545',
        });
      }
    }
  };

  const handleClearAll = () => {
    setSelectedItems([]);
    setShowActionDropdown(false);
  };

  const handleAddNew = () => {
    let shopIdToUse = selectedShop;
    if (shopIdToUse) {
      navigate(`/shop/${shopIdToUse}/AddIssuestock`);
    } else {
      navigate('/AddIssuestock');
    }
  };

  const handleEdit = (item) => {
    let shopIdToUse = selectedShop;
    if (shopIdToUse) {
      navigate(`/shop/${shopIdToUse}/AddIssuestock`, { state: { editData: item } });
    } else {
      navigate('/AddIssuestock', { state: { editData: item } });
    }
  };

  const handleDelete = async (itemId) => {
    const result = await Swal.fire({
      title: 'Delete Record?',
      text: 'Are you sure you want to delete this record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/inventory/materials/${itemId}/delete/`);
        clearIssuestockCache(selectedShop);
        await fetchIssuestockData();
        setSelectedItems(selectedItems.filter((id) => id !== itemId));

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Record deleted successfully!',
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error deleting record:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete record. Please try again.',
          confirmButtonColor: '#dc3545',
        });
      }
    }
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.isl-action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown]);

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="issuestocklist-main-container">
      {/* ===== HEADER: Title LEFT, Controls RIGHT ===== */}
      <div className="isl-header-section">
        <div className="isl-header-left">
          <h2 className="isl-header-title">
            <FaBoxTissue className="isl-title-icon" />
            <span>Issue Stock Management</span>
          </h2>

          {/* ✅ City count badge */}
          <span className="isl-city-badge">
            <FaMapMarkerAlt className="isl-city-badge-icon" />
            {selectedCityLabel} ({selectedCityCount})
          </span>
        </div>

        <div className="isl-header-right">
          <div className="isl-city-wrapper">
            <select
              className="isl-city-select"
              value={selectedCity}
              onChange={(e) => { setSelectedCity(e.target.value); setSelectedItems([]); }}
              disabled={loadingCities && cities.length === 0}
            >
              <option value="">
                {loadingCities && cities.length === 0 ? 'Loading...' : `All Cities (${totalCount})`}
              </option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[normCity(city)] || 0})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="isl-city-dropdown-arrow" />
          </div>

          <div className="isl-search-wrapper">
            <FaSearch className="isl-search-icon" />
            <input
              type="text"
              placeholder="Search by Lot Number or Material..."
              value={searchTerm}
              onChange={handleSearch}
              className="isl-search-input"
            />
          </div>

          <button className="isl-select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{selectedItems.length === filteredData.length && filteredData.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>
          <button className="isl-add-btn" onClick={handleAddNew}>
            <FaPlus />
            <span>ADD NEW ISSUE</span>
          </button>
        </div>
      </div>

      <div className="isl-content">
        {/* Selected Summary */}
        {selectedItems.length > 0 && (
          <div className="isl-selected-summary">
            <div className="isl-summary-content">
              <span className="isl-summary-icon">✓</span>
              <span className="isl-summary-text">
                {selectedItems.length} item(s) selected
              </span>
              <div className="isl-action-dropdown-container">
                <button
                  className="isl-action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="isl-action-dropdown-menu">
                    <button
                      className="isl-dropdown-item isl-delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedItems.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="isl-clear-selection" onClick={handleClearAll}>
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* ===== TABLE ===== */}
        <div className="isl-table-container">
          <div className="isl-table-wrapper">
            {filteredData.length > 0 ? (
              <table className="isl-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.length === filteredData.length && filteredData.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Internal Lot Number</th>
                    <th>Material</th>
                    <th>Issued Quantity</th>
                    <th>Used Quantity</th>
                    <th>Returned Quantity</th>
                    <th>Remaining Stock</th>
                    <th>Shop</th>
                    <th>City</th>
                    <th style={{ width: '110px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                    <tr key={item.id} className={selectedItems.includes(item.id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      </td>
                      <td>
                        {item.internal_lot_number || item.internal_supplier_number || item.lot_number || item.internal_lot_no || '-'}
                      </td>
                      <td>{item.material}</td>
                      <td>{item.issued_quantity}</td>
                      <td>{item.used_quantity || 0}</td>
                      <td>{item.returned_quantity}</td>
                      <td className={parseFloat(item.remaining_stock) < 100 ? 'isl-low-stock' : ''}>
                        {item.remaining_stock}
                      </td>
                      <td>{item.shop_name || '-'}</td>
                      <td>{item.city || '-'}</td>
                      <td className="isl-actions-cell">
                        <button
                          className="isl-edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit Item"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="isl-delete-btn"
                          onClick={() => handleDelete(item.id)}
                          title="Delete Item"
                        >
                          <RiDeleteBin5Fill />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : dataLoaded ? (
              <div className="isl-no-items">
                <FaBoxes className="isl-no-data-icon" />
                <p>
                  {!isAllCities
                    ? `No issue stock records found for city "${selectedCity}".`
                    : 'No issue stock records found.'}
                </p>
                <button className="isl-add-first-btn" onClick={handleAddNew}>
                  <FaPlus /> Add Your First Record
                </button>
              </div>
            ) : (
              <div style={{ minHeight: '300px' }}></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Issuestock;